import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import styles from './viewOrganisationMap.module.scss';
import { Col, Row, Spin } from 'antd';
import { OrganisationMapMode } from 'enums/organisationMapEnums';
import { useParentOrganizationContext } from 'context/ParentOrganisationContext';
import {
  OrganizationNodeType,
  generateNodesAndEdges,
} from '../OrganisationReactFlow/organisationReactFlow.util';
import OrganisationMapReactFlow, {
  OrganizationMapReset,
} from '../OrganisationReactFlow';
import { useParams } from 'react-router-dom';
import { NodeMouseHandler, Node, Edge } from 'reactflow';
import useRedirect from 'shared/utils/useRedirect';
import { deleteStepsInfo } from 'constants/organizationDetails.constants';
import { OrganizationDetails } from 'models/OrganizationDetails/organizationDetails.model';
import clsx from 'clsx';
import { useLocationsDataContext } from 'context/LocationsDataContext';
import { MapResetButton } from '../OrganisationReactFlow/resetOrganizationMap';
import { ZoomControls } from '../OrganisationReactFlow/zoomControls';
import OrganisationMapService from 'services/OrganisationService/organisationMap.service';

interface ViewOrganisationMapProps {
  year?: number;
  mapLoading?: boolean;
  isDetailsPage?: boolean;
  callBackTriggered?: boolean;
  organization?: OrganizationDetails;
  mapRef?: React.RefObject<OrganizationMapReset>;
}

export const DeleteContent = (
  <Row>
    <Col span={24} className={styles.additionalInfo}>
      Note: If you want to disable the organization, you can go inside the map
      and toggle it off
    </Col>
    <Col>
      {deleteStepsInfo.map((item, index) => (
        <Row className={styles.deleteContent} key={`deleteInfo${index}`}>
          <Col className={styles.deleteIndex}>
            <div className={styles.deleteIndexContent}>
              {(index + 1).toString().padStart(2, '0')}
            </div>
          </Col>
          <Col span={18} className={styles.deleteInfo}>
            {item}
          </Col>
        </Row>
      ))}
    </Col>
  </Row>
);

const ViewOrganisationMap: FC<ViewOrganisationMapProps> = ({
  year,
  mapLoading = false,
  isDetailsPage,
  callBackTriggered,
  organization,
  mapRef: detailsRef,
}) => {
  const { id: organisationId } = useParams();
  const { redirectToOrganizationDetails } = useRedirect();
  const { organisationMap, fetchOrganisationMap, loading } =
    OrganisationMapService();
  const { organizationData } = useParentOrganizationContext();
  const { clearLocationContext } = useLocationsDataContext();
  const mapRef = useRef<OrganizationMapReset>(null);
  const [collapsedNodes, setCollapsedNodes] = useState<Set<string>>(new Set());

  useEffect(() => {
    if ((isDetailsPage && year) || !isDetailsPage)
      fetchOrganisationMap(year ? { year } : {}, false, organisationId);
  }, [year, organisationId, callBackTriggered]);

  const deleteCallBack = async () =>
    (await fetchOrganisationMap({ year })) ?? {};

  const toggleNodeCollapse = (nodeId: string) => {
    setCollapsedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return newSet;
    });
  };

  const { nodes: initialNodes, edges: initialEdges } = useMemo(() => {
    return generateNodesAndEdges({
      data: organisationMap?.organizationMap ?? [],
      mode: OrganisationMapMode.VIEW,
      organisationBoundary:
        organizationData?.company?.organizationalBoundary ||
        organization?.company?.organizationalBoundary ||
        '',
      ...(!isDetailsPage && { deleteCallBack: deleteCallBack }),
      collapsedNodes,
      onToggleCollapse: toggleNodeCollapse,
    });
  }, [organisationMap, collapsedNodes]);

  // Filter nodes and edges based on collapsed state
  const { nodes, edges } = useMemo(() => {
    const visibleNodeIds = new Set<string>();
    const filteredNodes: Node[] = [];
    const filteredEdges: Edge[] = [];

    // Build a map of node to its parent for quick lookup
    const nodeToParent = new Map<string, string>();
    initialEdges.forEach(edge => {
      nodeToParent.set(edge.target, edge.source);
    });

    // Helper function to check if a node should be visible
    const isNodeVisible = (nodeId: string): boolean => {
      const parentId = nodeToParent.get(nodeId);
      if (!parentId) {
        // Root node - always visible
        return true;
      }

      // Check if parent is collapsed
      if (collapsedNodes.has(parentId)) {
        return false;
      }

      // Recursively check parent
      return isNodeVisible(parentId);
    };

    // First pass: identify all visible nodes
    initialNodes.forEach(node => {
      const orgNode = node as OrganizationNodeType;
      if (!orgNode.hidden && isNodeVisible(node.id)) {
        visibleNodeIds.add(node.id);
        filteredNodes.push(node);
      }
    });

    // Second pass: add edges only between visible nodes
    initialEdges.forEach(edge => {
      if (visibleNodeIds.has(edge.source) && visibleNodeIds.has(edge.target)) {
        filteredEdges.push(edge);
      }
    });

    return { nodes: filteredNodes, edges: filteredEdges };
  }, [initialNodes, initialEdges, collapsedNodes]);

  const handleNodeClick: NodeMouseHandler = (_, node) => {
    const organizationNode = node as OrganizationNodeType;
    if (
      !organizationNode.organizationId ||
      organizationNode.organizationId == organisationId
    )
      return;
    clearLocationContext();
    redirectToOrganizationDetails(organizationNode.organizationId, {
      year: year,
    });
  };
  const isParentOrg =
    organization?.organization?.organisationYearWise?.isParent;
  return (
    <Spin spinning={loading || mapLoading} className={styles.loadingContainer}>
      <Row
        className={clsx(styles.organisationMapWrapper, {
          [styles.parentDetailsPage]: isDetailsPage && isParentOrg,
          [styles.childDetailsPage]: isDetailsPage && !isParentOrg,
        })}>
        <Col span={24} className={styles.organisationMap}>
          {nodes.length ? (
            <>
              <OrganisationMapReactFlow
                initialNodes={nodes}
                initialEdges={edges}
                handleNodeClick={handleNodeClick}
                ref={detailsRef ?? mapRef}
              />
              <ZoomControls mapRef={detailsRef ?? mapRef} />
            </>
          ) : null}
        </Col>
        {!isDetailsPage && (
          <Col span={24}>
            <MapResetButton mapRef={mapRef} />
          </Col>
        )}
      </Row>
    </Spin>
  );
};

export default ViewOrganisationMap;
